import React, { useState } from "react";
import { Input, Select, Button, Spin, message, Image, Upload } from "antd";
import { ArrowLeftOutlined, PaperClipOutlined } from "@ant-design/icons";
import {
  useGetSupportTicketByIdQuery,
  useUpdateSupportTicketMutation,
  useReplyToSupportTicketMutation,
} from "../../api/authApi";
import uploadSupportFileToS3, {
  validateSupportFile,
} from "../../utils/supportS3Upload";
import {
  DetailPage,
  BackBtn,
  DetailHeader,
  DetailTitle,
  DetailSub,
  ActionGroup,
  Card,
  CardTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  Description,
  MetaList,
  MetaRow,
  MetaDt,
  MetaDd,
  Thread,
  Bubble,
  BubbleMeta,
  BubbleBody,
  EmptyThread,
  ReplySection,
  ReplyActions,
  AttachGrid,
  StatusPill,
} from "./styles";

const { TextArea } = Input;

const STATUS_STYLES = {
  OPEN: { bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
  IN_PROGRESS: { bg: "#ffedd5", color: "#c2410c", border: "#fed7aa" },
  WAITING_ON_CUSTOMER: { bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  RESOLVED: { bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  CLOSED: { bg: "#f3f4f6", color: "#4b5563", border: "#e5e7eb" },
};

const META_LABELS = {
  platform: "Platform",
  invoiceOrOrder: "Invoice / order number",
  accountEmail: "Account email",
  importance: "Importance",
};

const formatStatus = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCategory = (cat) =>
  String(cat || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const Attachments = ({ items }) => {
  if (!items?.length) return null;
  return (
    <AttachGrid>
      {items.map((a) =>
        a.mimeType?.startsWith("image/") ? (
          <Image key={a.id || a.url} src={a.url} alt={a.fileName} width={80} />
        ) : (
          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer">
            <PaperClipOutlined /> {a.fileName}
          </a>
        )
      )}
    </AttachGrid>
  );
};

const MetadataBlock = ({ metadata }) => {
  if (!metadata || !Object.keys(metadata).length) return null;
  return (
    <Card>
      <CardTitle>Additional details</CardTitle>
      <MetaList>
        {Object.entries(metadata).map(([key, value]) => (
          <MetaRow key={key}>
            <MetaDt>{META_LABELS[key] || key}</MetaDt>
            <MetaDd>{String(value)}</MetaDd>
          </MetaRow>
        ))}
      </MetaList>
    </Card>
  );
};

const TicketDetail = ({ ticketId, onBack }) => {
  const { data: ticket, isLoading } = useGetSupportTicketByIdQuery(ticketId);
  const [updateTicket] = useUpdateSupportTicketMutation();
  const [replyMutation, { isLoading: sending }] =
    useReplyToSupportTicketMutation();
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    const err = validateSupportFile(file);
    if (err) {
      message.error(err);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      const uploaded = await uploadSupportFileToS3(file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (e) {
      message.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleStatusChange = async (status) => {
    try {
      await updateTicket({ id: ticketId, status }).unwrap();
      message.success("Status updated");
    } catch {
      message.error("Failed to update status");
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await updateTicket({ id: ticketId, priority }).unwrap();
      message.success("Priority updated");
    } catch {
      message.error("Failed to update priority");
    }
  };

  const handleSend = async () => {
    if (!reply.trim()) return message.error("Enter a reply");
    try {
      await replyMutation({
        id: ticketId,
        body: reply.trim(),
        attachments: attachments.length ? attachments : undefined,
      }).unwrap();
      setReply("");
      setAttachments([]);
      message.success("Reply sent");
    } catch (e) {
      message.error(e?.data?.message || "Failed to send reply");
    }
  };

  if (isLoading) {
    return (
      <DetailPage>
        <Spin />
      </DetailPage>
    );
  }
  if (!ticket) {
    return (
      <DetailPage>
        <p>Ticket not found</p>
      </DetailPage>
    );
  }

  const statusStyle = STATUS_STYLES[ticket.status] || STATUS_STYLES.CLOSED;

  return (
    <DetailPage>
      <BackBtn type="button" onClick={onBack}>
        <ArrowLeftOutlined /> Back to tickets
      </BackBtn>

      <DetailHeader>
        <div>
          <DetailTitle>{ticket.referenceNumber}</DetailTitle>
          <DetailSub>
            Opened {new Date(ticket.createdAt).toLocaleString()}
          </DetailSub>
        </div>
        <ActionGroup>
          <Select
            value={ticket.status}
            onChange={handleStatusChange}
            style={{ width: 200 }}
            options={[
              "OPEN",
              "IN_PROGRESS",
              "WAITING_ON_CUSTOMER",
              "RESOLVED",
              "CLOSED",
            ].map((s) => ({ value: s, label: formatStatus(s) }))}
          />
          <Select
            value={ticket.priority}
            onChange={handlePriorityChange}
            style={{ width: 130 }}
            options={["Low", "Medium", "High", "Urgent"].map((p) => ({
              value: p,
              label: p,
            }))}
          />
        </ActionGroup>
      </DetailHeader>

      <Card>
        <CardTitle>Customer</CardTitle>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Name</InfoLabel>
            <InfoValue>{ticket.submitterName || "—"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{ticket.submitterEmail || "—"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>{ticket.submitterPhone || "—"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Category</InfoLabel>
            <InfoValue>{formatCategory(ticket.category)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>
              <StatusPill
                $bg={statusStyle.bg}
                $color={statusStyle.color}
                $border={statusStyle.border}
              >
                {formatStatus(ticket.status)}
              </StatusPill>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Priority</InfoLabel>
            <InfoValue>{ticket.priority}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </Card>

      <Card>
        <CardTitle>Issue description</CardTitle>
        <Description>{ticket.description}</Description>
        <Attachments items={ticket.initialAttachments || []} />
      </Card>

      <MetadataBlock metadata={ticket.metadata} />

      <Card>
        <CardTitle>Conversation</CardTitle>
        <Thread>
          {(ticket.messages || []).length === 0 && (
            <EmptyThread>No messages yet. Send a reply below.</EmptyThread>
          )}
          {(ticket.messages || []).map((m) => (
            <Bubble key={m.id} $admin={m.authorType === "ADMIN"}>
              <BubbleMeta>
                {m.authorType === "ADMIN" ? "You (Admin)" : "Customer"} ·{" "}
                {new Date(m.createdAt).toLocaleString()}
              </BubbleMeta>
              <BubbleBody>{m.body}</BubbleBody>
              <Attachments items={m.attachments} />
            </Bubble>
          ))}
        </Thread>
      </Card>

      <Card>
        <CardTitle>Reply to customer</CardTitle>
        <ReplySection>
          <TextArea
            rows={4}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write your reply…"
          />
          <ReplyActions>
            <Upload
              beforeUpload={handleUpload}
              fileList={attachments.map((a, i) => ({
                uid: String(i),
                name: a.fileName,
                status: "done",
              }))}
              onRemove={(f) =>
                setAttachments((prev) =>
                  prev.filter((a) => a.fileName !== f.name)
                )
              }
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              maxCount={5}
            >
              <Button icon={<PaperClipOutlined />} disabled={uploading}>
                Attach file
              </Button>
            </Upload>
            <Button
              type="primary"
              loading={sending}
              onClick={handleSend}
              style={{ background: "#ff6b35", borderColor: "#ff6b35" }}
            >
              Send reply
            </Button>
          </ReplyActions>
        </ReplySection>
      </Card>
    </DetailPage>
  );
};

export default TicketDetail;
